import type React from 'react';
import { useEffect, useState } from 'react';
import logoWhite from '@/assets/images/logo-white.png';
import { classNames, cva } from '@/lib/utils';
import { LoadingProgressBar } from '../LoadingProgressBar';
import { LoadingSpinner } from '../LoadingSpinner';
import type { LoadingOverlayProps } from './LoadingOverlay.types';

const loadingOverlayContentVariants = cva('flex flex-col items-center justify-center', {
  variants: {
    variant: {
      spinner: '',
      progress: 'w-[200px] h-[100px]',
    },
  },
  defaultVariants: {
    variant: 'spinner',
  },
});

const loadingOverlayWrapperVariants = cva('absolute inset-0 z-[1000] flex items-center justify-center', {
  variants: {
    variant: {
      spinner: 'bg-overlay-darker',
      progress: 'bg-overlay-darkest',
    },
  },
  defaultVariants: {
    variant: 'spinner',
  },
});

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  variant = 'spinner',
  spinnerSize = 'md',
  overlay = false,
  title,
  message,
  isVisible = true,
  className = '',
}) => {
  const [progress, setProgress] = useState(0);

  // Only animate progress for progress variant
  useEffect(() => {
    if (variant !== 'progress') {
      return;
    }

    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 95) {
          return 95;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 95);
      });
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, [variant]);

  if (!isVisible) {
    return null;
  }

  const spinnerSizeForOverlay = overlay && spinnerSize === 'md' ? 'lg' : spinnerSize;

  const content = (
    <div className={classNames(loadingOverlayContentVariants({ variant }), className)}>
      {variant === 'progress' ? (
        <>
          <img src={logoWhite} alt="SeaWatch Logo" className="w-[130px] h-[50px] mb-4 object-contain" />
          <LoadingProgressBar value={progress} />
        </>
      ) : (
        <>
          <LoadingSpinner size={spinnerSizeForOverlay} />
          {title && <div className="mt-4 text-white text-lg font-bold">{title}</div>}
          {message && <div className="mt-2 text-white text-sm">{message}</div>}
        </>
      )}
    </div>
  );

  if (!overlay) {
    return content;
  }

  return <div className={loadingOverlayWrapperVariants({ variant })}>{content}</div>;
};
